import { useNavigate } from '@tanstack/react-router'
import { Bone, BookOpenText, DatabaseZap, Drama, Home, ImageUp, LayoutGrid, Moon, Mountain, Search, Settings, Sparkles, SunMoon, TriangleAlert, Workflow, type LucideIcon } from 'lucide-react'
import { useEffect, useMemo, useRef, useState } from 'react'

import { startIndex } from '@/api/jobs'
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/components/ui/dialog'
import { toast } from '@/components/ui/toast'
import { cn } from '@/components/ui/utils'
import { describeCombo, registerShortcuts } from '@/lib/keys'
import { useUiStore } from '@/stores/uiStore'

/**
 * `Ctrl+K`: jump to a page or run an action. Later phases add "jump to
 * asset" (FTS search) and editor actions via `registerCommands()`.
 */

export interface Command {
  id: string
  label: string
  hint?: string
  icon: LucideIcon
  keywords?: string
  run: () => void | Promise<void>
}

type CommandFactory = (ctx: { navigate: ReturnType<typeof useNavigate> }) => Command[]

const extraCommands = new Set<CommandFactory>()

/** Register additional commands (returns the unregister). Used by feature modules. */
export function registerCommands(factory: CommandFactory): () => void {
  extraCommands.add(factory)
  return () => extraCommands.delete(factory)
}

function builtinCommands(navigate: ReturnType<typeof useNavigate>): Command[] {
  const ui = useUiStore.getState()
  return [
    { id: 'go.home', label: 'Home', icon: Home, keywords: 'start', run: () => navigate({ to: '/' }) },
    { id: 'go.new', label: 'Make a story', icon: Sparkles, keywords: 'wizard new create story', run: () => navigate({ to: '/stories/new', search: {} }) },
    { id: 'go.puppets', label: 'Puppets', hint: 'Characters, animals and props', icon: Drama, keywords: 'characters animals props bones skeleton rig move', run: () => navigate({ to: '/puppets', search: {} }) },
    { id: 'go.sprites', label: 'Sprites', hint: 'Sprite sheet characters and clips', icon: LayoutGrid, keywords: 'spritesheet animation frames editor characters', run: () => navigate({ to: '/sprites' }) },
    { id: 'go.mypuppets', label: 'My puppets', hint: 'Your own puppets', icon: Drama, keywords: 'mine uploads skeletons bones', run: () => navigate({ to: '/puppets', search: { tab: 'mine' } }) },
    { id: 'go.split', label: 'Split a picture into bones', hint: 'One picture -> a puppet', icon: Bone, keywords: 'upload image character rig auto bones skeleton split ai', run: () => navigate({ to: '/skel/split', search: {} }) },
    { id: 'go.sheet', label: 'New puppet from a parts sheet', icon: ImageUp, keywords: 'upload drawing splitter bones', run: () => navigate({ to: '/skel/new', search: {} }) },
    { id: 'go.backgrounds', label: 'Backgrounds', hint: 'Places, scenery and details', icon: Mountain, keywords: 'stages places scenery plates annotate backdrop world trees clouds', run: () => navigate({ to: '/backgrounds', search: {} }) },
    { id: 'go.backgrounds.look', label: 'Backgrounds that need a look', icon: TriangleAlert, keywords: 'stages places fix problems flags check', run: () => navigate({ to: '/backgrounds', search: { needs_look: true } }) },
    { id: 'go.stories', label: 'Stories', icon: BookOpenText, run: () => navigate({ to: '/stories', search: {} }) },
    { id: 'go.settings', label: 'Settings', icon: Settings, run: () => navigate({ to: '/settings', search: {} }) },
    { id: 'go.jobs', label: 'Activity', hint: 'Settings › Advanced', icon: Workflow, keywords: 'jobs queue', run: () => navigate({ to: '/jobs', search: {} }) },
    { id: 'go.vocab', label: 'Vocabulary', hint: 'Settings › Advanced', icon: Settings, keywords: 'kinds themes', run: () => navigate({ to: '/settings/vocabulary', search: {} }) },
    { id: 'go.library', label: 'Library', hint: 'Every picture, character, object and sound', icon: LayoutGrid, keywords: 'picture library assets browse places backgrounds characters sounds', run: () => navigate({ to: '/library', search: {} }) },
    {
      id: 'act.index',
      label: 'Refresh the picture library',
      hint: 'Full run',
      icon: DatabaseZap,
      keywords: 'index bucket reindex crawl',
      run: async () => {
        try {
          const r = await startIndex({ mode: 'full' })
          toast.success('Refreshing the picture library', { description: `Job #${r.job_id}` })
        } catch (err) {
          toast.error('Could not refresh the picture library', { description: err instanceof Error ? err.message : String(err) })
        }
      },
    },
    { id: 'act.theme', label: 'Cycle theme', hint: ui.theme, icon: ui.theme === 'dark' ? Moon : SunMoon, keywords: 'dark light', run: () => useUiStore.getState().cycleTheme() },
    { id: 'act.queue', label: ui.queuePanelOpen ? 'Hide queue panel' : 'Show queue panel', icon: Workflow, run: () => useUiStore.getState().setQueuePanelOpen(!ui.queuePanelOpen) },
  ]
}

function matches(cmd: Command, q: string): boolean {
  if (!q) return true
  const hay = `${cmd.label} ${cmd.hint ?? ''} ${cmd.keywords ?? ''}`.toLowerCase()
  return q
    .toLowerCase()
    .split(/\s+/)
    .every((w) => hay.includes(w))
}

export function CommandPalette() {
  const open = useUiStore((s) => s.paletteOpen)
  const setOpen = useUiStore((s) => s.setPaletteOpen)
  const navigate = useNavigate()
  const [query, setQuery] = useState('')
  const [cursor, setCursor] = useState(0)
  const listRef = useRef<HTMLUListElement>(null)

  useEffect(() => registerShortcuts('global', { '$mod+k': () => setOpen(!useUiStore.getState().paletteOpen) }, { alwaysActive: true }), [setOpen])

  const commands = useMemo(() => {
    if (!open) return []
    const all = [...builtinCommands(navigate), ...[...extraCommands].flatMap((f) => f({ navigate }))]
    return all.filter((c) => matches(c, query.trim()))
    // `open` is intentionally a dependency: builtins read live store state when opened.
  }, [open, query, navigate])

  useEffect(() => {
    if (!open) {
      setQuery('')
      setCursor(0)
    }
  }, [open])

  useEffect(() => {
    setCursor((c) => Math.min(c, Math.max(0, commands.length - 1)))
  }, [commands.length])

  useEffect(() => {
    listRef.current?.querySelector<HTMLElement>(`[data-index="${cursor}"]`)?.scrollIntoView({ block: 'nearest' })
  }, [cursor])

  const run = (cmd: Command | undefined) => {
    if (!cmd) return
    setOpen(false)
    void cmd.run()
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent size="md" hideClose className="top-[15%] translate-y-0 gap-0 p-0" onOpenAutoFocus={(e) => e.preventDefault()}>
        <DialogTitle className="sr-only">Command palette</DialogTitle>
        <DialogDescription className="sr-only">Type to filter pages and actions; Enter runs the highlighted one.</DialogDescription>
        <div className="flex items-center gap-2 border-b border-line px-4">
          <Search className="size-4 shrink-0 text-ink-3" aria-hidden />
          <input
            autoFocus
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'ArrowDown') {
                e.preventDefault()
                setCursor((c) => Math.min(c + 1, commands.length - 1))
              } else if (e.key === 'ArrowUp') {
                e.preventDefault()
                setCursor((c) => Math.max(c - 1, 0))
              } else if (e.key === 'Enter') {
                e.preventDefault()
                run(commands[cursor])
              }
            }}
            placeholder="Where to? Type a page or an action…"
            aria-label="Command"
            aria-activedescendant={commands[cursor] ? `cmd-${commands[cursor].id}` : undefined}
            aria-controls="command-list"
            role="combobox"
            aria-expanded
            className="h-14 w-full bg-transparent text-base text-ink outline-none placeholder:text-ink-3"
          />
          <kbd className="hidden rounded-md border border-line px-1.5 py-0.5 font-mono text-[10px] text-ink-3 sm:inline">{describeCombo('$mod+k')}</kbd>
        </div>
        <ul id="command-list" ref={listRef} role="listbox" className="max-h-80 overflow-y-auto p-2">
          {commands.length === 0 ? <li className="px-3 py-6 text-center text-sm text-ink-3">Nothing matches "{query}"</li> : null}
          {commands.map((cmd, i) => {
            const Icon = cmd.icon
            return (
              <li
                key={cmd.id}
                id={`cmd-${cmd.id}`}
                role="option"
                aria-selected={i === cursor}
                data-index={i}
                onMouseEnter={() => setCursor(i)}
                onClick={() => run(cmd)}
                className={cn('flex min-h-11 cursor-pointer items-center gap-3 rounded-xl px-3 text-sm text-ink', i === cursor && 'bg-paper-2')}
              >
                <Icon className="size-4 shrink-0 text-ink-2" aria-hidden />
                <span className="flex-1 truncate">{cmd.label}</span>
                {cmd.hint ? <span className="truncate text-xs text-ink-3">{cmd.hint}</span> : null}
              </li>
            )
          })}
        </ul>
      </DialogContent>
    </Dialog>
  )
}
